import { isLocalStorageAvailable } from './local-storage-availability'
import { reportError } from '@/services/error-reporting'

const DEFAULT_APP_ENV = 'development'
const environment = import.meta.env.VITE_APP_ENV || DEFAULT_APP_ENV
const VERSION_KEY = `assets-version-${environment}`
const VERSION_LOCK_KEY = `assets-version-lock-${environment}`

export const startVersionChecker = async (): Promise<void> => {
  if (!isLocalStorageAvailable()) {
    return
  }

  const checkInterval = import.meta.env.VITE_ASSETS_CHECK_SECONDS || '300'
  const interval = 1000 * parseInt(checkInterval)
  localStorage.removeItem(VERSION_LOCK_KEY)

  checkAssetsVersion()

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkAssetsVersion()
    }
  })

  setInterval(() => {
    checkAssetsVersion()
  }, interval)
}

async function checkAssetsVersion() {
  if (localStorage.getItem(VERSION_LOCK_KEY)) return

  localStorage.setItem(VERSION_LOCK_KEY, 'true')

  if (environment && environment !== DEFAULT_APP_ENV) {
    try {
      const localVersion = localStorage.getItem(VERSION_KEY)

      if (localVersion) {
        const response = await fetch(`/version.txt?t=${Date.now()}`)
        const remoteVersion = await response.text()

        if (response.ok) {
          localStorage.removeItem(VERSION_LOCK_KEY)

          if (remoteVersion.length <= 20 && localVersion !== remoteVersion) {
            localStorage.setItem(VERSION_KEY, remoteVersion)
            setTimeout(() => window.location.reload(), 1000)
          }
        }
      } else {
        await getAndStoreRemoteVersion()
      }
    } catch (error) {
      localStorage.removeItem(VERSION_LOCK_KEY)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'

      if (errorMessage.includes('Failed to fetch')) return

      reportError(error, {
        name: 'Error checking version',
        context: {
          error,
          environment: environment
        },
        tags: 'version-checker'
      })
    }
  }
}

async function getAndStoreRemoteVersion(retries: number = 0) {
  try {
    const response = await fetch(`/version.txt?t=${Date.now()}`)
    const remoteVersion = await response.text()

    if (response.ok && remoteVersion.length <= 20) {
      localStorage.setItem(VERSION_KEY, remoteVersion)
      await checkAssetsVersion()
      return
    }
  } catch (error) {
    // Do nothing
  }

  if (retries > 5) {
    localStorage.removeItem(VERSION_LOCK_KEY)
    return
  } else {
    setTimeout(
      async () => {
        await getAndStoreRemoteVersion(retries + 1)
      },
      1000 * (retries + 1)
    )
  }
}

export const getLocalVersion = (): string => {
  try {
    return localStorage.getItem(VERSION_KEY) || 'no-local-version'
  } catch (_error) {
    return 'unknown-version'
  }
}
